import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Divider
} from "@mui/material";
import { getActivePatchNotes } from "../../components/utilities/PatchNotesUtilities";
import MarkdownViewer from "../../components/utilities/MarkdownViewer";
import ListResponsive from "../../components/other/ListResponsive";

export default function PatchNotesPage() {
  const [patches, setPatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const fetchPatches = async () => {
      try {
        const data = await getActivePatchNotes();
        const sortedData = [...data].sort(
          (a, b) => new Date(b.publishedDate) - new Date(a.publishedDate)
        );
        setPatches(sortedData);
      } catch (err) {
        console.error("Error fetching patch notes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPatches();
  }, []);

  const handleToggleExpand = (patchId) => {
    setExpandedId(expandedId === patchId ? null : patchId);
  };

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h3" gutterBottom>
            Patch Notes
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            View all updates and improvements made to the platform.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          {!loading && patches.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
              No patch notes available yet.
            </Typography>
          ) : (
            <ListResponsive
              originalList={loading ? null : patches}
              doPages
              initialRowsPerPage={10}
              doSearch
              searchProps={{
                label: "Search Patch Notes",
                performSearch: (originalList, searchText) => {
                  if (!searchText || searchText === "") {
                    return originalList;
                  }
                  return originalList.filter((patch) => {
                    if (patch?.title?.toLowerCase().includes(searchText))
                      return true;
                    if (patch?.content?.toLowerCase().includes(searchText))
                      return true;
                    return false;
                  });
                }
              }}
              scrollToTop
              spacing={2}
              rowProps={{
                mapFunction: (patch, index) => (
                  <Grid item xs={12} key={patch.id || index}>
                    <Paper
                      sx={{
                        p: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: 3
                        }
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Grid
                            container
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={1}
                          >
                            <Grid item xs={12} sm>
                              <Typography variant="h5" gutterBottom>
                                {patch.title}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {new Date(
                                  patch.publishedDate
                                ).toLocaleDateString(undefined, {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric"
                                })}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm="auto">
                              <Button
                                variant={
                                  expandedId === patch.id
                                    ? "contained"
                                    : "outlined"
                                }
                                color="primary"
                                onClick={() => handleToggleExpand(patch.id)}
                                fullWidth={window.innerWidth < 600}
                              >
                                {expandedId === patch.id
                                  ? "Hide Details"
                                  : "View Details"}
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>

                        {expandedId === patch.id && (
                          <>
                            <Grid item xs={12}>
                              <Divider />
                            </Grid>
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  "& > *": {
                                    mb: 1
                                  },
                                  "& h1, & h2, & h3, & h4, & h5, & h6": {
                                    mt: 2,
                                    mb: 1
                                  }
                                }}
                              >
                                <MarkdownViewer source={patch.content} />
                              </Box>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                )
              }}
            />
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
